import React, { useState, useEffect } from 'react'
import { Modal, message } from 'antd'
import ReactPlayer from 'react-player'
import {
  DownloadOutlined,
  ShareAltOutlined,
  FullscreenOutlined,
  CloseOutlined
} from '@ant-design/icons'

import './AiVideoModal.scss'

const AIVideoModal = ({
  visible,
  onClose,
  videoUrl,
  title = 'AI Generated Video'
}) => {
  const [isLoading, setIsLoading] = useState(true)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [downloadLoading, setDownloadLoading] = useState(false)

  const handleDownload = async () => {
    setDownloadLoading(true)

    try {
      const response = await fetch(videoUrl, {
        method: 'GET',
        headers: {
          Accept: 'video/mp4,video/webm,video/*'
        }
      })

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`)

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.download = `${title.replace(/[^a-zA-Z0-9]/g, '_')}_${Date.now()}.mp4`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      message.success('Video downloaded successfully!')
    } catch (error) {
      console.error('Download failed:', error)
      try {
        const link = document.createElement('a')
        link.href = videoUrl
        link.target = '_blank'
        link.download = `${title.replace(
          /[^a-zA-Z0-9]/g,
          '_'
        )}_${Date.now()}.mp4`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        message.info(
          'Download started - if it fails, the URL may not support it'
        )
      } catch {
        window.open(videoUrl, '_blank', 'noopener,noreferrer')
        message.warning('Opened video in new tab. Right-click to save.')
      }
    } finally {
      setDownloadLoading(false)
    }
  }

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title,
          text: 'Check out this AI generated video!',
          url: videoUrl
        })
        message.success('Shared successfully!')
      } else {
        await navigator.clipboard.writeText(videoUrl)
        message.success('Video URL copied to clipboard!')
      }
    } catch (err) {
      console.error('Share failed:', err)
      try {
        await navigator.clipboard.writeText(videoUrl)
        message.success('Video URL copied to clipboard!')
      } catch {
        message.error('Unable to share or copy URL')
      }
    }
  }

  const toggleFullscreen = () => {
    const element = document.querySelector('.ai-video-modal .ant-modal-content')
    if (!document.fullscreenElement) {
      element && element.requestFullscreen && element.requestFullscreen()
    } else {
      document.exitFullscreen && document.exitFullscreen()
    }
  }

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      width="90vw"
      centered
      className="ai-video-modal"
      closable={false}
      bodyStyle={{ padding: 0 }}
    >
      <div className="ai-video-container">
        {/* Header */}
        <div className="video-header visible">
          <div className="video-title">
            <span>{title}</span>
          </div>
          <div className="header-actions">
            <button className="action-btn" onClick={handleShare} title="Share">
              <ShareAltOutlined />
            </button>
            <button
              className="action-btn"
              onClick={handleDownload}
              title="Download"
              disabled={downloadLoading}
            >
              <DownloadOutlined />
              {downloadLoading && <span className="loading-dot">...</span>}
            </button>
            <button
              className="action-btn"
              onClick={toggleFullscreen}
              title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
            >
              <FullscreenOutlined />
            </button>
            <button className="close-btn" onClick={onClose} title="Close">
              <CloseOutlined />
            </button>
          </div>
        </div>

        {/* Loading Overlay */}
        {isLoading && (
          <div className="loading-overlay">
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>Loading your AI creation...</p>
            </div>
          </div>
        )}

        {/* Video Player */}
        <div className={`video-wrapper ${isFullscreen ? 'fullscreen' : ''}`}>
          <ReactPlayer
            url={videoUrl}
            width="100%"
            height="100%"
            playing
            controls
            onReady={() => setIsLoading(false)}
            onError={error => {
              console.error('Video player error:', error)
              setIsLoading(false)
            }}
            config={{
              file: {
                attributes: {
                  controlsList: 'nodownload',
                  disablePictureInPicture: true,
                  crossOrigin: 'anonymous'
                }
              }
            }}
          />
        </div>

        {/* Background Blur Effect */}
        <div
          className="background-blur"
          style={{ backgroundImage: `url(${videoUrl})` }}
        ></div>
      </div>
    </Modal>
  )
}

export default AIVideoModal
